// ---------------------------------------------------------------------------
// Wire types — the exact shapes returned by the BE vendor-preferences endpoints.
// The index endpoint returns a BARE nested tree (no `.data` wrapper); the
// eligible-vendors endpoint and the write actions are unwrapped per their own
// shapes (see api.ts / extra-reducer.ts for where each is unwrapped).
// ---------------------------------------------------------------------------

// A vendor with its rank inside a ranking list. `rank` is authoritative on the
// wire; the FE also derives display order from array position when writing.
// `is_eligible` is a real BE contract field: `index` reads return it per ranked
// vendor (`false` for a vendor that was ranked but has since lost its market /
// skill — surfaced in place at its rank, not dropped), and the write responses
// carry it (always `true`). It flows through normalization by passthrough.
// Kept OPTIONAL so the slice degrades gracefully while the backend change is
// unmerged: a deployed BE predating it omits the field → `undefined`, which
// consumers treat as eligible/unknown.
export type RankedVendor = {
  vendor_uuid: string;
  name: string;
  rank: number;
  is_eligible?: boolean;
};

export type ApiSubCategory = {
  problem_sub_category_uuid: string;
  label: string;
  slug: string;
  state: 'no_eligible_vendor' | 'override' | 'inherits';
  vendors: RankedVendor[];
};

/**
 * The level a trade's vendor list comes from (6.6): the most specific list that exists wins —
 * property, then market, then company. The market screen only ever sees `market` or `company`.
 */
export type VendorListLevel = 'property' | 'market' | 'company';

export type ApiCategory = {
  problem_category_uuid: string;
  label: string;
  slug: string;
  status: 'pending' | 'configured';
  // `market`: the market's own list; `company`: the company list, inherited; null: no list.
  // Optional: a backend predating company lists omits it.
  default_source?: Extract<VendorListLevel, 'market' | 'company'> | null;
  override_count: number;
  default_vendors: RankedVendor[];
  sub_categories: ApiSubCategory[];
};

// GET api/vendor-preferences?market_uuid=… — bare nested tree.
export type VendorPreferenceIndexResponse = {
  market_uuid: string;
  pending_count: number;
  categories: ApiCategory[];
};

// GET api/vendor-preferences/eligible-vendors?… — unwrapped from `payload.data`.
export type EligibleVendor = {
  vendor_uuid: string;
  name: string;
};

// Flat resource returned by setCategoryDefault / setSubCategoryOverride /
// customizeSubCategory. resetSubCategoryToDefault returns a MESSAGE, not this.
export type VendorPreferenceListResource = {
  vendor_preference_list_uuid: string;
  is_override: boolean;
  is_empty: boolean;
  vendors: RankedVendor[];
};

// GET api/vendor-preferences/company — the company's trade lists (6.6, Q14). Trade-only (Q9): the
// market config's category keys minus `override_count` and `sub_categories`, and no market.
// `default_source` is `company` when the trade has a company list (even an empty one), else null.
export type ApiCompanyCategory = {
  problem_category_uuid: string;
  label: string;
  slug: string;
  status: 'pending' | 'configured';
  default_source: 'company' | null;
  default_vendors: RankedVendor[];
};

export type CompanyVendorPreferenceResponse = {
  pending_count: number;
  categories: ApiCompanyCategory[];
};

// ---------------------------------------------------------------------------
// Normalized node types — the slice flattens the wire tree into id-keyed maps.
// ---------------------------------------------------------------------------

export type CategoryNode = {
  problem_category_uuid: string;
  label: string;
  slug: string;
  status: 'pending' | 'configured';
  /** Where `defaultRanking` comes from: the market's own list, the company list, or none. */
  defaultSource: Extract<VendorListLevel, 'market' | 'company'> | null;
  overrideCount: number;
  defaultRanking: RankedVendor[];
  subCategoryIds: string[];
};

export type SubCategoryNode = {
  problem_sub_category_uuid: string;
  problem_category_uuid: string;
  label: string;
  slug: string;
  state: 'no_eligible_vendor' | 'override' | 'inherits';
  ranking: RankedVendor[];
};

/** One trade at the company level (6.6, Q14). */
export type CompanyCategoryNode = {
  problem_category_uuid: string;
  label: string;
  slug: string;
  status: 'pending' | 'configured';
  /** The trade has a company list (even an empty one) — the only case Reset can remove. */
  hasCompanyList: boolean;
  ranking: RankedVendor[];
};

/**
 * The "Company (all markets)" view's own state. Kept apart from the market tree and its caches so
 * the market guards (keyed on `activeMarketUuid`) and the company view can never mix data.
 */
export type CompanyVendorPreferencesState = {
  categoriesById: Record<string, CompanyCategoryNode>;
  categoryOrder: string[];
  pendingCount: number;
  hasLoaded: boolean;
  isLoading: boolean;
  loadError: VendorPreferenceError;
  /** The company pool per trade (eligible in at least one of the company's markets). */
  eligibleByCategory: Record<string, EligibleVendor[]>;
  eligibleLoadingByCategory: Record<string, boolean>;
  /** Terminal per-trade failure, so the fetch effect can't retry in a loop. */
  eligibleErrorByCategory: Record<string, boolean>;
  isSaving: boolean;
  saveError: VendorPreferenceError;
};

export const companyVendorPreferencesInitialState: CompanyVendorPreferencesState = {
  categoriesById: {},
  categoryOrder: [],
  pendingCount: 0,
  hasLoaded: false,
  isLoading: false,
  loadError: null,
  eligibleByCategory: {},
  eligibleLoadingByCategory: {},
  eligibleErrorByCategory: {},
  isSaving: false,
  saveError: null,
};

// Per-concern error shape; mirrors the scheduling slice's `{ status?, message? }`.
// `request()` rejects with `rejectWithValue({ ...response.data, status })`, so
// `action.payload` carries both fields.
export type VendorPreferenceError = {
  status?: number;
  message?: string;
} | null;

export type VendorPreferencesState = {
  // Normalized tree.
  categoriesById: Record<string, CategoryNode>;
  // Keyed `${categoryUuid}:${subUuid}`.
  subCategoriesById: Record<string, SubCategoryNode>;
  categoryOrder: string[];
  /**
   * The market whose tree is CURRENTLY LOADED into `categoriesById` / `subCategoriesById`.
   * Set ONLY by `fetchVendorPreferences.fulfilled`. Distinct from `activeMarketUuid`
   * (the market the UI wants): the two diverge while a market-switch fetch is in flight.
   * The cross-market guards in extra-reducer.ts compare against `activeMarketUuid` to
   * drop responses that arrive after the user has moved on.
   */
  marketUuid: string | null;
  pendingCount: number;
  // Eligible vendors per slot, keyed `${categoryUuid}:${subUuid ?? 'default'}`.
  eligibleVendorsByKey: Record<string, EligibleVendor[]>;
  // Per-slot eligible-loading flags, keyed identically to `eligibleVendorsByKey`
  // (the eligible picker opens per node and rapid switching means a consumer must
  // know WHICH slot is loading — a single global boolean can't express that).
  eligibleLoadingByKey: Record<string, boolean>;
  // Per-slot TERMINAL failure sentinel, keyed identically to `eligibleVendorsByKey`.
  // A non-canceled `fetchEligibleVendors` rejection sets this so the fetch effect
  // stops re-firing (the slot has neither data nor a loading flag, which would
  // otherwise re-satisfy the "missing" predicate → an unbounded retry storm).
  // Cleared on the next pending/fulfilled for the key, so retry is explicit.
  eligibleErrorByKey: Record<string, boolean>;
  // UI focus pointers.
  /**
   * The market the UI currently WANTS to show. Set immediately by `setActiveMarket`
   * (the UI intent), so it may LEAD `marketUuid` during an in-flight market-switch
   * fetch. This is the authority the cross-market guards trust; `selectIsTreeStale`
   * exposes the `activeMarketUuid !== marketUuid` window to consumers.
   */
  activeMarketUuid: string | null;
  activeCategoryUuid: string | null;
  activeSubCategoryUuid: string | null;
  // Per-concern loading + error flags.
  isConfigLoading: boolean;
  isSaving: boolean;
  configError: VendorPreferenceError;
  eligibleError: VendorPreferenceError;
  saveError: VendorPreferenceError;
  /** The "Company (all markets)" view (6.6, Q14). Not reset by a market switch. */
  company: CompanyVendorPreferencesState;
};

export const vendorPreferencesInitialState: VendorPreferencesState = {
  categoriesById: {},
  subCategoriesById: {},
  categoryOrder: [],
  marketUuid: null,
  pendingCount: 0,
  eligibleVendorsByKey: {},
  eligibleLoadingByKey: {},
  eligibleErrorByKey: {},
  activeMarketUuid: null,
  activeCategoryUuid: null,
  activeSubCategoryUuid: null,
  isConfigLoading: false,
  isSaving: false,
  configError: null,
  eligibleError: null,
  saveError: null,
  company: companyVendorPreferencesInitialState,
};
